<script setup lang="ts">
import { initWebPush } from '@/plugins/webpush'

const router = useRouter()
const authStore = useAuthStore()
const jwtToken = authStore.token

const isSupported = ref(false)
const permissionState = ref<'default' | 'granted' | 'denied'>('default')
const isSubscribed = ref(false)
const loading = ref(false)

onMounted(async () => {
    try {
        isSupported.value = 'Notification' in window && 'serviceWorker' in navigator
        permissionState.value = Notification.permission

        if (!isSupported.value) {
            return
        }

        const registration = await navigator.serviceWorker.getRegistration('/js/webpush-sw.js')
        const sub = await registration?.pushManager.getSubscription()
        if (sub) {
            isSubscribed.value = true
        }
    } catch (err) {
        console.error('WebPush check failed:', err)
    }
})

async function enablePush() {
    if (!isSupported.value) return
    loading.value = true

    try {
        const webPushClient = await initWebPush(
            'BACTatYpKIpNqGPlSmGmh1pyFqLusIQ2ORBbAb4V44v3ksR9l9fCfcVuws2g9RSv5EJVSYE4eEGUGSztL03VJT4',
            '/webpush/',
            jwtToken
        )

        const state = await webPushClient.getPermissionState()
        permissionState.value = state

        if (state === 'granted') {
            const sub = await webPushClient.getSubscription()
            if (!sub) await webPushClient.subscribe()
            isSubscribed.value = true
        }
    } catch (err) {
        console.error('Push subscription failed:', err)
    } finally {
        loading.value = false
    }
}

function goToSettings() {
    router.push({ name: 'settings', hash: '#push' })
}

const statusColor = computed(() => {
    if (!isSupported.value) return 'warning'
    if (permissionState.value === 'granted' && isSubscribed.value) return 'success'
    if (permissionState.value === 'denied') return 'error'
    return 'grey'
})

const statusText = computed(() => {
    if (!isSupported.value) return 'Nicht unterstützt'
    if (permissionState.value === 'granted' && isSubscribed.value) return 'Aktiviert'
    if (permissionState.value === 'denied') return 'Blockiert'
    return 'Inaktiv'
})

const statusIcon = computed(() => {
    if (!isSupported.value) return 'mdi-alert-circle-outline'
    if (permissionState.value === 'granted' && isSubscribed.value) return 'mdi-bell-ring'
    if (permissionState.value === 'denied') return 'mdi-bell-off'
    return 'mdi-bell-outline'
})
</script>

<template>
    <v-card hover @click="goToSettings" class="cursor-pointer">
        <v-card-text>
            <div class="d-flex align-center mb-3">
                <v-avatar :color="statusColor" size="48" class="mr-3">
                    <v-icon :icon="statusIcon" size="24" color="white"></v-icon>
                </v-avatar>
                <div class="flex-grow-1">
                    <div class="text-caption text-medium-emphasis">Push-Benachrichtigungen</div>
                    <div class="text-h6 font-weight-medium">{{ statusText }}</div>
                </div>
            </div>

            <v-divider class="mb-3"></v-divider>

            <div class="d-flex justify-space-between align-center">
                <div class="text-body-2 text-medium-emphasis">
                    {{ isSupported && !isSubscribed && permissionState !== 'denied'
                        ? 'Aktiviere Push-Benachrichtigungen'
                        : 'Details anzeigen' }}
                </div>

                <v-btn
                    v-if="isSupported && !isSubscribed && permissionState !== 'denied'"
                    color="primary"
                    size="small"
                    :loading="loading"
                    @click.stop="enablePush"
                    variant="tonal"
                >
                    Aktivieren
                </v-btn>
                <v-icon v-else>mdi-chevron-right</v-icon>
            </div>
        </v-card-text>
    </v-card>
</template>

<style scoped>
.cursor-pointer {
    cursor: pointer;
    transition: transform 0.2s ease;
}

.cursor-pointer:hover {
    transform: translateY(-2px);
}
</style>